import { Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { Person } from './Person';
import {
  USERS_I_CAN_SEE_USER,
  USERS_I_CAN_SEE_ADMIN,
  USERS_I_CAN_UPDATE_ADMIN,
  USERS_I_CAN_UPDATE_USER,
} from './SecurityRules';

export interface UserRow {
  id: string;
  email: string;
  status: string;
  last_active: string;
  created_at: string;
  person_id: number;
  receive_event_reminders: string;
  receive_unread_msg_emails: boolean;
  skip_all_email_sending: boolean;
  privacy_policy_accepted_version: number | null;
  platform_terms_accepted_version: number | null;
  booking_cancellation_refund_terms_accepted_version: number | null;
  host_payments_payouts_terms_accepted_version: number | null;
  unsuccessful_login_attempts: number | null;
  stripe_connect_account_id: string | null;
  stripe_connect_details_submitted: boolean;
  stripe_connect_charges_enabled: boolean;
  stripe_connect_payouts_enabled: boolean;
  stripe_connect_onboarded_at: string | null;
  pwa_last_prompt_at: string | null;
  pwa_last_dismissed_at: string | null;
  pwa_install_accepted_at: string | null;
  pwa_installed_at: string | null;
  pwa_status: string;
}

export const User: Entity<UserRow> = {
  id: 'User',
  title: 'Member',
  service: 'main',
  rowLabel: (row: UserRow) => row.email,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.user',
  },
  security: [
    {
      role: 'user',
      action: 'read',
      access: 'conditional',
      condition: "status = 'ACTIVE' AND id IN " + USERS_I_CAN_SEE_USER,
    },
    {
      role: 'user',
      action: 'all',
      access: 'conditional',
      condition: "status = 'ACTIVE' AND id IN " + USERS_I_CAN_UPDATE_USER,
    },
    {
      role: 'admin',
      action: 'read',
      access: 'conditional',
      condition: "status = 'ACTIVE' AND id IN " + USERS_I_CAN_SEE_ADMIN,
    },
    {
      role: 'admin',
      action: 'all',
      access: 'conditional',
      condition: "status = 'ACTIVE' AND id IN " + USERS_I_CAN_UPDATE_ADMIN,
    },
    { role: 'staff', action: 'all', access: 'conditional', condition: "status = 'ACTIVE'" },
  ],
  defaultSort: [['email', 'ASC']],
  defaultFilters: [{ fieldId: 'email', operator: 'ct', value: '' }],
  listRowLink: '/admin/user/display/{key}',
  fields: {
    id: {
      seq: 5,
      type: FieldType.Text,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      // listColumn: true,
      // formColumnWidths: { xs: 12, lg: 6 },
      visible: false,
    },
    email: {
      seq: 10,
      type: FieldType.Text,
      label: 'Email Address',
      persistence: PersistenceType.BackEndManaged,
      formColumnWidths: { xs: 12, lg: 6 },
    },
    status: {
      seq: 12,
      label: 'Status',
      type: FieldType.Options,
      mandatory: true,
      persistence: PersistenceType.BackEndOnly,
      options: [
        { id: 'ACTIVE', label: 'active' },
        { id: 'INACTIVE', label: 'inactive' },
      ],
    },
    last_active: {
      seq: 15,
      type: FieldType.Datetime,
      label: 'Last Active',
      persistence: PersistenceType.BackEndManaged,
    },
    created_at: {
      seq: 20,
      type: FieldType.Datetime,
      label: 'Created At',
      persistence: PersistenceType.BackEndManaged,
    },
    person_id: {
      seq: 25,
      type: FieldType.Reference,
      label: 'Person',
      refEntity: Person,
      persistence: PersistenceType.BackEndManaged,
    },
    receive_event_reminders: {
      seq: 30,
      type: FieldType.Options,
      label: 'Receive Event Reminders?',
      fieldGroup: 'display',
      options: [
        { id: 'all', label: 'all' },
        { id: 'trackedOnly', label: 'for tracked events only' },
        { id: 'none', label: 'none' },
      ],
    },
    receive_unread_msg_emails: {
      seq: 32,
      type: FieldType.Boolean,
      label: 'Receive Unread Message Emails',
      persistence: PersistenceType.BackEndManaged,
    },
    skip_all_email_sending: {
      seq: 33,
      type: FieldType.Boolean,
      label: 'Skip All Email Sending',
      fieldGroup: 'display',
      // persistence: PersistenceType.BackEndManaged,
    },
    privacy_policy_accepted_version: {
      seq: 35,
      label: 'Privacy Policy Accepted Version',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    platform_terms_accepted_version: {
      seq: 40,
      label: 'Platform Terms Accepted Version',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    booking_cancellation_refund_terms_accepted_version: {
      seq: 45,
      label: 'Booking, Cancellation and Refund Terms Accepted Version',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    host_payments_payouts_terms_accepted_version: {
      seq: 50,
      label: 'Host Payments and Payouts Terms Accepted Version',
      type: FieldType.Integer,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    unsuccessful_login_attempts: {
      seq: 52,
      label: 'Unsuccessful Login Attempts',
      type: FieldType.Integer,
      // persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    stripe_connect_account_id: {
      seq: 55,
      label: 'Stripe Connect Account Id',
      type: FieldType.Text,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    stripe_connect_details_submitted: {
      seq: 60,
      label: 'Stripe Connect Details Submitted',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    stripe_connect_charges_enabled: {
      seq: 65,
      label: 'Stripe Connect Charges Enabled',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    stripe_connect_payouts_enabled: {
      seq: 70,
      label: 'Stripe Connect Payouts Enabled',
      type: FieldType.Boolean,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    stripe_connect_onboarded_at: {
      seq: 75,
      label: 'Stripe Connect Onboarded At',
      type: FieldType.Datetime,
      persistence: PersistenceType.BackEndManaged,
      visible: false,
    },
    pwa_last_prompt_at: {
      seq: 80,
      type: FieldType.Datetime,
      label: 'PWA Last Prompted',
      persistence: PersistenceType.BackEndManaged,
    },
    pwa_last_dismissed_at: {
      seq: 85,
      type: FieldType.Datetime,
      label: 'PWA Last Dismissed',
      persistence: PersistenceType.BackEndManaged,
    },
    pwa_install_accepted_at: {
      seq: 90,
      type: FieldType.Datetime,
      label: 'PWA Install Accepted',
      persistence: PersistenceType.BackEndManaged,
    },
    pwa_installed_at: {
      seq: 95,
      type: FieldType.Datetime,
      label: 'PWA Installed',
      persistence: PersistenceType.BackEndManaged,
    },
    pwa_status: {
      seq: 100,
      type: FieldType.Options,
      label: 'PWA Status',
      persistence: PersistenceType.BackEndManaged,
      options: [
        { id: 'not_prompted', label: 'Not Prompted' },
        { id: 'prompted', label: 'Prompted' },
        { id: 'dismissed', label: 'Dismissed' },
        { id: 'accepted', label: 'Accepted' },
        { id: 'installed', label: 'Installed' },
      ],
    },
  },
};
